import type {
  TaskTrackerBoardSummary,
  TaskTrackerBoardSummaryMutationResult,
  TaskTrackerBoardSummaryPreview,
  TaskTrackerBoardSummarySavePayload,
  TaskTrackerSummaryColumnsProjectConfig,
  TaskTrackerSummaryFilterGroup,
  TaskTrackerSummaryFilterLeaf,
  TaskTrackerSummaryRule,
  TaskTrackerSummarySortingConfig,
} from '../lib/taskTrackerBoardSummary.types'
import { attachPrimarySummarySelection } from '../lib/taskTrackerBoardSummary.mappers'
import { mapBoardCardsFromApi } from './taskTracker.boards.api.mappers'
import type {
  ApiTaskTrackerSummary,
  ApiTaskTrackerSummaryFilterGroup,
  ApiTaskTrackerSummaryFilterLeaf,
  ApiTaskTrackerSummaryMutationResponse,
  ApiTaskTrackerSummaryPreview,
  ApiTaskTrackerSummaryRule,
  ApiTaskTrackerSummarySaveBody,
  ApiTaskTrackerSummarySorting,
} from './taskTracker.api.types'

const isApiSummaryFilterGroup = (
  value: ApiTaskTrackerSummaryFilterLeaf | ApiTaskTrackerSummaryFilterGroup,
): value is ApiTaskTrackerSummaryFilterGroup =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Array.isArray((value as ApiTaskTrackerSummaryFilterGroup).items)

const normalizeApiSummaryFilterGroups = (
  raw: unknown,
): ApiTaskTrackerSummaryFilterGroup[] => {
  const flatten = (value: unknown): ApiTaskTrackerSummaryFilterGroup[] => {
    if (Array.isArray(value)) {
      return value.flatMap((item) => flatten(item))
    }

    if (isApiSummaryFilterGroup(value as ApiTaskTrackerSummaryFilterGroup)) {
      return [value as ApiTaskTrackerSummaryFilterGroup]
    }

    return []
  }

  return flatten(raw)
}

const mapSummaryFilterLeafFromApi = (
  item: ApiTaskTrackerSummaryFilterLeaf,
): TaskTrackerSummaryFilterLeaf | null => {
  const field = String(item.field ?? '').trim()
  if (!field) return null

  if (field === 'status') {
    const value = String(item.value ?? '').trim()
    if (value !== 'completed' && value !== 'not_completed') return null
    return { field: 'status', value }
  }

  if (field === 'performer') {
    const rawValues = item.values ?? item.value
    const values = (Array.isArray(rawValues) ? rawValues : [rawValues])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)

    if (values.length === 0) return null
    return { field: 'performer', values }
  }

  if (field === 'deadline') {
    const operator = String(item.operator ?? '').trim()
    if (!operator) return null

    if (operator === 'within_days') {
      const days = Number(item.days)
      if (!Number.isFinite(days) || days <= 0) return null

      return {
        field: 'deadline',
        operator: 'within_days',
        days: Math.trunc(days),
      }
    }

    return {
      field: 'deadline',
      operator: operator as Extract<
        TaskTrackerSummaryFilterLeaf,
        { field: 'deadline' }
      >['operator'],
      from: item.from ?? null,
      to: item.to ?? null,
    }
  }

  if (field === 'sticker') {
    const fieldSettingId = Number(item.field_setting_id)
    const value = String(item.value ?? '').trim()
    const from = String(item.from ?? '').trim()
    const to = String(item.to ?? '').trim()
    const rawValues = item.values ?? item.value
    const values = (Array.isArray(rawValues) ? rawValues : [rawValues])
      .map((value) => String(value).trim())
      .filter(Boolean)

    if (
      !Number.isFinite(fieldSettingId) ||
      fieldSettingId <= 0 ||
      (values.length === 0 && !value && !from && !to)
    ) {
      return null
    }

    return {
      field: 'sticker',
      fieldSettingId,
      ...(value ? { value } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(values.length > 0 && !value && !from && !to ? { values } : {}),
    }
  }

  return null
}

const mapSummaryFilterGroupFromApi = (
  group: ApiTaskTrackerSummaryFilterGroup,
): TaskTrackerSummaryFilterGroup => ({
  logic: group.logic === 'or' ? 'or' : 'and',
  items: (group.items ?? [])
    .map((item) => {
      if (isApiSummaryFilterGroup(item)) {
        return mapSummaryFilterGroupFromApi(item)
      }

      return mapSummaryFilterLeafFromApi(item)
    })
    .filter(
      (
        item,
      ): item is TaskTrackerSummaryFilterLeaf | TaskTrackerSummaryFilterGroup =>
        item != null,
    ),
})

const mapSummaryFiltersConfigFromApi = (
  raw: unknown,
): TaskTrackerSummaryFilterGroup[] =>
  normalizeApiSummaryFilterGroups(raw).map(mapSummaryFilterGroupFromApi)

const mapSummaryFilterLeafToApi = (
  item: TaskTrackerSummaryFilterLeaf,
): ApiTaskTrackerSummaryFilterLeaf => {
  if (item.field === 'status') {
    return { field: item.field, value: item.value }
  }

  if (item.field === 'performer') {
    return { field: item.field, values: item.values }
  }

  if (item.field === 'deadline') {
    if (item.operator === 'within_days') {
      return {
        field: item.field,
        operator: item.operator,
        days: item.days,
      }
    }

    return {
      field: item.field,
      operator: item.operator,
      from: item.from ?? undefined,
      to: item.to ?? undefined,
    }
  }

  if (item.field === 'sticker') {
    return {
      field: item.field,
      field_setting_id: item.fieldSettingId,
      ...(item.value ? { value: item.value } : {}),
      ...(item.from ? { from: item.from } : {}),
      ...(item.to ? { to: item.to } : {}),
      ...(item.values ? { values: item.values } : {}),
    }
  }

  throw new Error('Unsupported summary filter leaf')
}

const mapSummaryFilterGroupToApi = (
  group: TaskTrackerSummaryFilterGroup,
): ApiTaskTrackerSummaryFilterGroup => ({
  logic: group.logic ?? 'and',
  items: (group.items ?? []).map((item) => {
    if ('items' in item && Array.isArray(item.items)) {
      return mapSummaryFilterGroupToApi(item)
    }

    return mapSummaryFilterLeafToApi(item as TaskTrackerSummaryFilterLeaf)
  }),
})

const mapSummaryColumnsConfigFromApi = (
  projects: ApiTaskTrackerSummary['columns_config'] = [],
): TaskTrackerSummaryColumnsProjectConfig[] =>
  (projects ?? []).map(
    (project): TaskTrackerSummaryColumnsProjectConfig => ({
      projectId: Number(project.project_id),
      boards: (project.boards ?? []).map((board) => ({
        boardId: Number(board.board_id),
        columnIds: [...new Set((board.column_ids ?? []).map(Number))],
      })),
    }),
  )

const mapSummaryColumnsConfigToApi = (
  projects: TaskTrackerSummaryColumnsProjectConfig[],
) =>
  projects.map((project) => ({
    project_id: project.projectId,
    boards: project.boards.map((board) => ({
      board_id: board.boardId,
      column_ids: [...new Set(board.columnIds)],
    })),
  }))

const mapSummaryRuleFromApi = (
  rule: ApiTaskTrackerSummaryRule,
): TaskTrackerSummaryRule => ({
  columnsConfig: mapSummaryColumnsConfigFromApi(rule.columns_config ?? []),
  filtersConfig: mapSummaryFiltersConfigFromApi(rule.filters_config ?? []),
})

const mapSummaryRuleToApi = (
  rule: TaskTrackerSummaryRule,
): ApiTaskTrackerSummaryRule => ({
  columns_config: mapSummaryColumnsConfigToApi(rule.columnsConfig),
  filters_config: rule.filtersConfig.map(mapSummaryFilterGroupToApi),
})

const mapSummarySortingListFromApi = (
  sorting:
    | ApiTaskTrackerSummarySorting
    | ApiTaskTrackerSummarySorting[]
    | null
    | undefined,
): TaskTrackerSummarySortingConfig[] => {
  if (!sorting) return []

  const list = Array.isArray(sorting) ? sorting : [sorting]

  return list
    .filter((item) => String(item?.sort_by ?? '').trim())
    .map((item) => ({
      sortBy: String(item.sort_by),
      sortOrder: item.sort_order === 'desc' ? 'desc' : 'asc',
    }))
}

const mapSummarySortingListToApi = (
  sorting: TaskTrackerSummarySortingConfig[],
): ApiTaskTrackerSummarySorting[] =>
  sorting
    .filter((item) => String(item.sortBy ?? '').trim())
    .map((item) => ({
      sort_by: item.sortBy,
      sort_order: item.sortOrder === 'desc' ? 'desc' : 'asc',
    }))

const resolveSummaryRulesConfigFromApi = (
  item: ApiTaskTrackerSummary,
): TaskTrackerSummaryRule[] => {
  if (item.rules_config?.length) {
    return item.rules_config.map(mapSummaryRuleFromApi)
  }

  const columnsConfig = mapSummaryColumnsConfigFromApi(item.columns_config ?? [])
  const filtersConfig = mapSummaryFiltersConfigFromApi(item.filters_config ?? [])

  if (columnsConfig.length === 0 && filtersConfig.length === 0) {
    return []
  }

  return [
    {
      columnsConfig,
      filtersConfig,
    },
  ]
}

export const mapTaskTrackerSummaryFromApi = (
  item: ApiTaskTrackerSummary,
): TaskTrackerBoardSummary => {
  const rulesConfig = resolveSummaryRulesConfigFromApi(item)
  const legacyColumnsConfig =
    rulesConfig[0]?.columnsConfig ??
    mapSummaryColumnsConfigFromApi(item.columns_config ?? [])
  const legacyFiltersConfig =
    rulesConfig[0]?.filtersConfig ??
    mapSummaryFiltersConfigFromApi(item.filters_config ?? [])

  return attachPrimarySummarySelection({
    id: Number(item.id),
    userId: Number(item.user_id),
    title: String(item.title ?? '').trim(),
    displayBoardId:
      item.board_id != null && Number.isFinite(Number(item.board_id))
        ? Number(item.board_id)
        : null,
    rulesConfig,
    columnsConfig: legacyColumnsConfig,
    filtersConfig: legacyFiltersConfig,
    sortingConfig: mapSummarySortingListFromApi(item.sorting_config),
    createdAt: Number(item.created_at ?? 0),
    updatedAt: Number(item.updated_at ?? 0),
    user: item.user
      ? {
          id: Number(item.user.id),
          name: String(item.user.name ?? '').trim(),
        }
      : null,
  })
}

export const mapTaskTrackerSummarySavePayloadToApi = (
  payload: TaskTrackerBoardSummarySavePayload,
): ApiTaskTrackerSummarySaveBody => ({
  title: payload.title.trim(),
  rules_config: payload.rulesConfig.map(mapSummaryRuleToApi),
  sorting_config: mapSummarySortingListToApi(payload.sortingConfig),
})

export const mapTaskTrackerSummaryPreviewFromApi = (
  item: ApiTaskTrackerSummaryPreview,
): TaskTrackerBoardSummaryPreview => ({
  summary: mapTaskTrackerSummaryFromApi(item.summary),
  items: mapBoardCardsFromApi(item.items),
  totalCount: Number(item.total_count ?? item.items?.length ?? 0),
  hasMore: Boolean(item.cards_has_more),
  nextCursor: item.next_cursor ?? null,
})

export const mapTaskTrackerSummaryMutationFromApi = (
  item: ApiTaskTrackerSummaryMutationResponse,
): TaskTrackerBoardSummaryMutationResult => ({
  summary: mapTaskTrackerSummaryFromApi(item.summary),
  preview: mapTaskTrackerSummaryPreviewFromApi(item.preview),
})
